// Picking a section for an item without AI:
//   1. what the household taught it (moving an item remembers where it goes),
//   2. the built-in dictionary — the whole name, then its parts, last words first
//      ("organic baby spinach" → "baby spinach"; "milk organic" → "milk"),
//   3. the same with one or two letters off ("brocoli", "bananna").

import { dictionaryIndex } from './dictionary';
import { itemKey, singularize, words } from './normalize';
import { FALLBACK_CATEGORY } from './categories';

export type CategorySource = 'memory' | 'dictionary' | 'fuzzy' | 'none';

export interface CategoryGuess {
  categoryId: string;
  source: CategorySource;
  /** The part of the name that matched. */
  matched: string | null;
}

/** Restricted Damerau-Levenshtein distance, giving up (returning max + 1) once it's over `max`. */
export function editDistance(a: string, b: string, max = 2): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const n = a.length;
  const m = b.length;
  let prev2: number[] = new Array(m + 1).fill(0);
  let prev: number[] = Array.from({ length: m + 1 }, (_, j) => j);
  for (let i = 1; i <= n; i++) {
    const cur = new Array<number>(m + 1);
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= m; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev2 = prev;
    prev = cur;
  }
  return prev[m];
}

/** How many typos a word of this length may have and still count as the same word. */
export function allowedTypos(word: string): number {
  if (word.length < 4) return 0;
  if (word.length < 8) return 1;
  return 2;
}

const keyOf = (s: string) => words(s).map(singularize).join(' ');
export const dictionary = () => dictionaryIndex(keyOf);

/** Contiguous word runs, longest first, and for equal length the later ones first. */
function phrases(ws: string[]): string[] {
  const out: string[] = [];
  for (let len = ws.length; len >= 1; len--) {
    for (let start = ws.length - len; start >= 0; start--) out.push(ws.slice(start, start + len).join(' '));
  }
  return out;
}

export interface CategorizeOptions {
  /** key → category the household chose (from memories). */
  learned?: Map<string, string>;
  /** Category ids that exist (a learned or dictionary answer for a deleted section is ignored). */
  known?: Set<string>;
}

export function categorize(name: string, opts: CategorizeOptions = {}): CategoryGuess {
  const key = itemKey(name);
  const ok = (id: string | undefined) => !!id && (!opts.known || opts.known.has(id));
  if (!key) return { categoryId: FALLBACK_CATEGORY, source: 'none', matched: null };
  const learned = opts.learned ?? new Map<string, string>();
  const dict = dictionary();

  // the household's choice for the exact name beats everything
  const exact = learned.get(key);
  if (ok(exact)) return { categoryId: exact!, source: 'memory', matched: key };

  const ws = key.split(' ');
  for (const p of phrases(ws)) {
    const l = learned.get(p);
    if (ok(l)) return { categoryId: l!, source: 'memory', matched: p };
    const d = dict.get(p);
    if (ok(d)) return { categoryId: d!, source: 'dictionary', matched: p };
  }

  // nothing exact: allow a typo or two, per word
  for (const p of phrases(ws)) {
    if (p.replace(/ /g, '').length < 4) continue;
    let best: { id: string; d: number; term: string } | null = null;
    const max = allowedTypos(p);
    for (const source of [learned, dict]) {
      for (const [term, id] of source) {
        if (!ok(id) || Math.abs(term.length - p.length) > max) continue;
        const d = editDistance(p, term, max);
        if (d <= max && (!best || d < best.d)) best = { id, d, term };
      }
    }
    if (best) return { categoryId: best.id, source: 'fuzzy', matched: best.term };
  }
  return { categoryId: ok(FALLBACK_CATEGORY) || !opts.known ? FALLBACK_CATEGORY : [...opts.known][0], source: 'none', matched: null };
}
